import { clientStore } from 'src/services/axiosInstance'
import { getStoreConfig } from 'src/store-config/get-config'

export type ReviewProductPreview = {
  name: string
  imageUrl: string
  imageAlt: string
}

type PreviewProductItem = {
  manufacturerCode?: string
  images?: Array<{ imageUrl?: string }>
}

type PreviewProduct = {
  productName?: string
  productReference?: string
  items?: PreviewProductItem[]
}

type SearchProductsResponse = {
  products?: PreviewProduct[]
}

function pickMatchingProduct(products: PreviewProduct[], code: string) {
  const normalizedCode = code.trim()

  return (
    products.find((product) =>
      product.items?.some((item) => item.manufacturerCode === normalizedCode)
    ) ??
    products.find((product) => product.productReference === normalizedCode) ??
    products[0]
  )
}

function pickMatchingItem(product: PreviewProduct, code: string) {
  const normalizedCode = code.trim()

  return (
    product.items?.find((item) => item.manufacturerCode === normalizedCode) ??
    product.items?.[0]
  )
}

export function resolveReviewProductPreview(
  products: PreviewProduct[] | undefined,
  code: string
): ReviewProductPreview | null {
  if (!products?.length) {
    return null
  }

  const product = pickMatchingProduct(products, code)
  const item = product ? pickMatchingItem(product, code) : undefined
  const name = product?.productName?.trim() ?? ''
  const imageUrl = item?.images?.[0]?.imageUrl?.trim() ?? ''

  if (!name && !imageUrl) {
    return null
  }

  return {
    name,
    imageUrl,
    imageAlt: name ? `Foto de ${name}` : 'Foto do produto',
  }
}

export async function fetchReviewProductPreview(
  code: string
): Promise<ReviewProductPreview | null> {
  const normalizedCode = code.trim()

  if (!normalizedCode) {
    return null
  }

  const storeConfig = getStoreConfig()
  const searchParams = new URLSearchParams({
    engine: 'vtex',
    sc: String(storeConfig.salesChannel),
    term: normalizedCode,
    page: '1',
    resultsPerPage: '5',
    sort: 'relevance',
  })

  try {
    const { data } = await clientStore.get<SearchProductsResponse>(
      `/_v3/api/decathlon-search/product/search?${searchParams.toString()}`
    )

    return resolveReviewProductPreview(data?.products, normalizedCode)
  } catch {
    return null
  }
}
